/**
 * Auto-atualização da versão desktop, servida pela VPS.
 *
 * COMO FUNCIONA (e o que ele NÃO faz):
 *
 * O updater do Neutralino baixa e substitui apenas o `resources.neu` — o pacote
 * com o app React. Ele NÃO troca o executável nem o neutralino.config.json.
 * Por isso o manifesto carrega dois números:
 *
 *   version        -> versão do resources.neu. Diferente da local ⇒ atualiza sozinho.
 *   minBinary      -> versão mínima do executável exigida por esse resources.
 *                     Se o .exe instalado for mais antigo, atualizar deixaria o app
 *                     quebrado; então avisamos para reinstalar em vez de instalar.
 *
 * Sem o minBinary, um resources novo que dependa de permissão nativa ausente no
 * .exe antigo instalaria com sucesso e só falharia em runtime — difícil de achar.
 */

import { isDesktop, appVersion } from './desktop';

export const URL_MANIFESTO =
  process.env.REACT_APP_UPDATE_MANIFEST ||
  'https://consulta.seellbr.com/releases/manifest.json';

/** Compara versões "1.2.10" numericamente. >0 se a > b. */
export const compararVersoes = (a, b) => {
  const pa = String(a || '0').split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '0').split('.').map((n) => parseInt(n, 10) || 0);
  const tam = Math.max(pa.length, pb.length);
  for (let i = 0; i < tam; i += 1) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d > 0 ? 1 : -1;
  }
  return 0;
};

/**
 * Consulta o manifesto. Nunca lança: offline ou VPS fora do ar devolvem
 * { estado: 'indisponivel' } e o app segue normalmente.
 *
 * Estados: 'atualizado' | 'disponivel' | 'exige-reinstalacao' | 'indisponivel'
 */
export const verificarAtualizacao = async () => {
  if (!isDesktop() || !window.Neutralino) return { estado: 'indisponivel' };

  const local = appVersion();
  try {
    // checkForUpdates valida o applicationId; se não bater, lança.
    const manifesto = await window.Neutralino.updater.checkForUpdates(URL_MANIFESTO);

    if (compararVersoes(manifesto.version, local) <= 0) {
      return { estado: 'atualizado', versaoLocal: local };
    }

    const binarioLocal = window.NL_VERSION;
    if (manifesto.minBinary && compararVersoes(manifesto.minBinary, binarioLocal) > 0) {
      return {
        estado: 'exige-reinstalacao',
        versaoLocal: local,
        versaoNova: manifesto.version,
        binarioLocal,
        binarioExigido: manifesto.minBinary,
        urlInstalador: manifesto.installerURL || null,
      };
    }

    return { estado: 'disponivel', versaoLocal: local, versaoNova: manifesto.version };
  } catch (e) {
    console.warn('Verificação de atualização indisponível:', e?.message || e);
    return { estado: 'indisponivel', erro: e?.message || String(e) };
  }
};

/**
 * Baixa e aplica o resources.neu, depois reinicia o app.
 * Só chame após verificarAtualizacao() devolver 'disponivel'.
 */
export const aplicarAtualizacao = async () => {
  if (!isDesktop() || !window.Neutralino) return false;
  // install() exige que checkForUpdates tenha rodado antes nesta mesma sessão.
  await window.Neutralino.updater.checkForUpdates(URL_MANIFESTO);
  await window.Neutralino.updater.install();
  await window.Neutralino.app.restartProcess();
  return true;
};
